import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Upload, Image, Film, Type, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import './Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ICON_OPTIONS = ['Users', 'Music', 'Utensils', 'MapPin', 'Clock', 'Star'];
const TABS = [
  { key: 'hero', label: 'Hero' },
  { key: 'gallery', label: 'Galerie' },
  { key: 'features', label: 'Vorteile' },
  { key: 'contact', label: 'Kontakt' },
  { key: 'footer', label: 'Footer' },
];

const adminFetch = async (endpoint, options = {}) => {
  const token = localStorage.getItem('adminToken');
  if (!token) { window.location.href = '/admin/login'; return null; }
  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers },
  });
  if (res.status === 401) { localStorage.removeItem('adminToken'); window.location.href = '/admin/login'; return null; }
  return res;
};

const uploadImage = async (file) => {
  const token = localStorage.getItem('adminToken');
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_URL}/api/admin/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (res.ok) return await res.json();
  return null;
};

const resolveImageUrl = (img) => {
  if (!img) return '';
  if (img.storagePath) return `${API_URL}/api/public/files/${img.storagePath}`;
  if (typeof img === 'string') {
    return img.startsWith('/api/') ? `${API_URL}${img}` : img;
  }
  return img.url || '';
};

const AdminContent = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('hero');
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin/login'); return; }
    loadAllContent();
  }, [navigate]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { if (meta.parentNode) meta.parentNode.removeChild(meta); };
  }, []);

  const loadAllContent = async () => {
    for (const tab of TABS) {
      const res = await adminFetch(`/api/admin/content/${tab.key}`);
      if (res && res.ok) {
        const data = await res.json();
        setContent(prev => ({ ...prev, [tab.key]: data }));
      }
    }
  };

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 3000); };

  const saveContent = async (key) => {
    setSaving(true);
    const res = await adminFetch(`/api/admin/content/${key}`, {
      method: 'PUT',
      body: JSON.stringify(content[key]),
    });
    if (res && res.ok) showFlash(`${key.charAt(0).toUpperCase() + key.slice(1)} gespeichert`);
    setSaving(false);
  };

  const updateContent = (key, field, value) => {
    setContent(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const triggerUpload = (target) => {
    setUploadTarget(target);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadTarget) return;
    setUploading(true);
    const result = await uploadImage(file);
    if (result && result.success) {
      if (uploadTarget.type === 'hero') {
        updateContent('hero', 'imageUrl', result.publicUrl);
      } else if (uploadTarget.type === 'gallery') {
        const images = [...(content.gallery?.images || [])];
        const idx = images.findIndex(i => i.id === uploadTarget.id);
        if (idx >= 0) {
          images[idx] = { ...images[idx], url: result.publicUrl, storagePath: result.path };
        }
        updateContent('gallery', 'images', images);
      }
      showFlash('Bild hochgeladen');
    }
    setUploading(false);
    e.target.value = '';
  };

  const moveGalleryImage = (idx, direction) => {
    const images = [...(content.gallery?.images || [])];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= images.length) return;
    [images[idx], images[newIdx]] = [images[newIdx], images[idx]];
    images.forEach((img, i) => { img.order = i; });
    updateContent('gallery', 'images', images);
  };

  const updateGalleryImage = (idx, field, value) => {
    const images = [...(content.gallery?.images || [])];
    images[idx] = { ...images[idx], [field]: value };
    updateContent('gallery', 'images', images);
  };

  const updateFeatureItem = (idx, field, value) => {
    const items = [...(content.features?.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    updateContent('features', 'items', items);
  };

  const hero = content.hero || {};
  const gallery = content.gallery || {};
  const features = content.features || {};
  const contact = content.contact || {};
  const footer = content.footer || {};

  return (
    <div className="admin-dashboard" data-testid="admin-content-page">
      {flash && <div className="admin-success-flash">{flash}</div>}
      <input type="file" ref={fileInputRef} accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />

      <div className="admin-topbar">
        <div className="admin-topbar-left">
          <button className="admin-back-nav" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft size={18} /> Dashboard
          </button>
          <span className="admin-topbar-logo">Inhalte verwalten</span>
        </div>
        <div className="admin-topbar-right">
          <button className="admin-export-btn" onClick={() => saveContent(activeTab)} disabled={saving || uploading} data-testid="save-content-btn">
            <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      <div className="admin-main">
        <div className="content-tabs" data-testid="content-tabs">
          {TABS.map(tab => (
            <button key={tab.key} className={`content-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}>{tab.label}</button>
          ))}
        </div>

        {/* HERO EDITOR */}
        {activeTab === 'hero' && (
          <div className="content-editor" data-testid="hero-editor">
            <div className="editor-section">
              <h3>Hero-Typ</h3>
              <div className="editor-toggle">
                <button className={`toggle-btn ${hero.heroType === 'image' ? 'active' : ''}`}
                  onClick={() => updateContent('hero', 'heroType', 'image')} data-testid="hero-type-image"><Image size={16} /> Bild</button>
                <button className={`toggle-btn ${hero.heroType === 'video' ? 'active' : ''}`}
                  onClick={() => updateContent('hero', 'heroType', 'video')} data-testid="hero-type-video"><Film size={16} /> Video</button>
              </div>
            </div>
            {hero.heroType !== 'video' && (
              <div className="editor-section">
                <h3>Hero-Bild</h3>
                <div className="image-upload-area" data-testid="hero-image-upload">
                  {hero.imageUrl && <img src={resolveImageUrl(hero.imageUrl)} alt="Hero" className="preview-image" />}
                  <button className="upload-btn" onClick={() => triggerUpload({ type: 'hero' })} disabled={uploading}>
                    <Upload size={16} /> {uploading ? 'Hochladen...' : 'Bild hochladen'}
                  </button>
                </div>
                <div className="editor-field">
                  <label>Alt-Text (SEO)</label>
                  <input type="text" value={hero.imageAlt || ''} onChange={(e) => updateContent('hero', 'imageAlt', e.target.value)} data-testid="hero-alt" />
                </div>
              </div>
            )}
            {hero.heroType === 'video' && (
              <div className="editor-section">
                <h3>Video-URL (YouTube oder Vimeo)</h3>
                <div className="editor-field">
                  <input type="url" value={hero.videoUrl || ''} onChange={(e) => updateContent('hero', 'videoUrl', e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..." data-testid="hero-video-url" />
                </div>
              </div>
            )}
            <div className="editor-section">
              <h3>Texte</h3>
              <div className="editor-field">
                <label>Headline</label>
                <input type="text" value={hero.headline || ''} onChange={(e) => updateContent('hero', 'headline', e.target.value)} data-testid="hero-headline" />
              </div>
              <div className="editor-field">
                <label>Subheadline</label>
                <input type="text" value={hero.subheadline || ''} onChange={(e) => updateContent('hero', 'subheadline', e.target.value)} data-testid="hero-subheadline" />
              </div>
              <div className="editor-field">
                <label>Button 1 Text</label>
                <input type="text" value={hero.button1Text || ''} onChange={(e) => updateContent('hero', 'button1Text', e.target.value)} data-testid="hero-btn1" />
              </div>
              <div className="editor-field">
                <label>Button 2 Text</label>
                <input type="text" value={hero.button2Text || ''} onChange={(e) => updateContent('hero', 'button2Text', e.target.value)} data-testid="hero-btn2" />
              </div>
            </div>
          </div>
        )}

        {/* GALLERY EDITOR */}
        {activeTab === 'gallery' && (
          <div className="content-editor" data-testid="gallery-editor">
            <div className="editor-section">
              <h3>Section-Texte</h3>
              <div className="editor-field">
                <label>Label</label>
                <input type="text" value={gallery.sectionLabel || ''} onChange={(e) => updateContent('gallery', 'sectionLabel', e.target.value)} />
              </div>
              <div className="editor-field">
                <label>Titel</label>
                <input type="text" value={gallery.sectionTitle || ''} onChange={(e) => updateContent('gallery', 'sectionTitle', e.target.value)} />
              </div>
              <div className="editor-field">
                <label>Beschreibung</label>
                <textarea value={gallery.sectionDescription || ''} onChange={(e) => updateContent('gallery', 'sectionDescription', e.target.value)} rows={2} />
              </div>
            </div>
            <div className="editor-section">
              <h3>Galerie-Bilder</h3>
              <div className="gallery-editor-grid" data-testid="gallery-images-editor">
                {(gallery.images || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((img, idx) => (
                  <div key={img.id} className="gallery-edit-card">
                    <div className="gallery-edit-preview">
                      <img src={resolveImageUrl(img)} alt={img.alt || img.title} />
                      <button className="gallery-edit-upload-btn" onClick={() => triggerUpload({ type: 'gallery', id: img.id })} disabled={uploading}>
                        <Upload size={14} /> Ersetzen
                      </button>
                    </div>
                    <div className="gallery-edit-fields">
                      <input type="text" placeholder="Titel" value={img.title || ''} onChange={(e) => updateGalleryImage(idx, 'title', e.target.value)} />
                      <input type="text" placeholder="Alt-Text (SEO)" value={img.alt || ''} onChange={(e) => updateGalleryImage(idx, 'alt', e.target.value)} />
                    </div>
                    <div className="gallery-edit-actions">
                      <button onClick={() => moveGalleryImage(idx, -1)} disabled={idx === 0} title="Nach oben"><ArrowUp size={14} /></button>
                      <span className="gallery-order-num">{idx + 1}</span>
                      <button onClick={() => moveGalleryImage(idx, 1)} disabled={idx === (gallery.images || []).length - 1} title="Nach unten"><ArrowDown size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FEATURES EDITOR */}
        {activeTab === 'features' && (
          <div className="content-editor" data-testid="features-editor">
            <div className="editor-section">
              <h3>Section-Texte</h3>
              <div className="editor-field">
                <label>Titel</label>
                <input type="text" value={features.sectionTitle || ''} onChange={(e) => updateContent('features', 'sectionTitle', e.target.value)} />
              </div>
              <div className="editor-field">
                <label>Beschreibung</label>
                <input type="text" value={features.sectionDescription || ''} onChange={(e) => updateContent('features', 'sectionDescription', e.target.value)} />
              </div>
            </div>
            <div className="editor-section">
              <h3>Vorteile / Features</h3>
              <div className="features-editor-list" data-testid="features-items-editor">
                {(features.items || []).map((item, idx) => (
                  <div key={idx} className="feature-edit-card">
                    <div className="feature-edit-row">
                      <select value={item.icon || 'Star'} onChange={(e) => updateFeatureItem(idx, 'icon', e.target.value)} className="feature-icon-select">
                        {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                      <input type="text" placeholder="Titel" value={item.title || ''} onChange={(e) => updateFeatureItem(idx, 'title', e.target.value)} className="feature-title-input" />
                    </div>
                    <textarea placeholder="Beschreibung" value={item.description || ''} onChange={(e) => updateFeatureItem(idx, 'description', e.target.value)} rows={2} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CONTACT EDITOR */}
        {activeTab === 'contact' && (
          <div className="content-editor" data-testid="contact-editor">
            <div className="editor-section">
              <h3>Kontaktformular-Texte</h3>
              <div className="editor-field">
                <label>Titel</label>
                <input type="text" value={contact.sectionTitle || ''} onChange={(e) => updateContent('contact', 'sectionTitle', e.target.value)} />
              </div>
              <div className="editor-field">
                <label>Beschreibung</label>
                <textarea value={contact.sectionDescription || ''} onChange={(e) => updateContent('contact', 'sectionDescription', e.target.value)} rows={3} />
              </div>
            </div>
          </div>
        )}

        {/* FOOTER EDITOR */}
        {activeTab === 'footer' && (
          <div className="content-editor" data-testid="footer-editor">
            <div className="editor-section">
              <h3>Footer-Texte</h3>
              <div className="editor-field">
                <label>Beschreibung</label>
                <textarea value={footer.description || ''} onChange={(e) => updateContent('footer', 'description', e.target.value)} rows={2} />
              </div>
              <div className="editor-field">
                <label>Strasse</label>
                <input type="text" value={footer.address || ''} onChange={(e) => updateContent('footer', 'address', e.target.value)} />
              </div>
              <div className="editor-field">
                <label>PLZ + Stadt</label>
                <input type="text" value={footer.city || ''} onChange={(e) => updateContent('footer', 'city', e.target.value)} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContent;
